import React, { useState, useEffect } from 'react';
import { Box, AlertTriangle, Clock, ArrowUpDown } from 'lucide-react';
import { getStockSummary } from '../../services/api';

const formatCurrency = (a) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 }).format(a);
const formatDT = (iso) => { if (!iso) return ''; const d = new Date(iso); return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' }); };

const ACTION_LABELS = { product_created: 'Ürün Eklendi', product_updated: 'Ürün Güncellendi', product_deleted: 'Ürün Silindi', stock_updated: 'Stok Güncellendi' };

export default function StockReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getStockSummary().then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-8 flex justify-center"><div className="w-5 h-5 border-2 border-[#6b6de3] border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return null;

  return (
    <div className="space-y-3">
      {/* Summary */}
      <div className="ios-card">
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-[#F2F2F7] rounded-xl p-3 text-center">
            <Box size={18} className="text-[#AF52DE] mx-auto mb-1" />
            <p className="text-2xl font-bold text-gray-900">{data.total_products}</p>
            <p className="text-[10px] text-[#8E8E93]">Toplam Ürün</p>
          </div>
          <div className="bg-[#F2F2F7] rounded-xl p-3 text-center">
            <p className="text-[10px] text-[#8E8E93] mb-1">Stok Değeri</p>
            <p className="text-2xl font-bold text-[#34C759]">{formatCurrency(data.total_value)}</p>
            <p className="text-[10px] text-[#8E8E93]">(maliyet × adet)</p>
          </div>
        </div>
      </div>

      {/* Critical Stock */}
      {data.critical?.length > 0 && (
        <div className="ios-card">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={14} className="text-[#FF3B30]" />
            <p className="text-xs font-semibold text-[#FF3B30] uppercase tracking-wider">Kritik Stok ({data.critical.length})</p>
          </div>
          <div className="space-y-1">
            {data.critical.map((p, i) => (
              <div key={i} className="flex items-center justify-between px-2.5 py-1.5 bg-red-50 rounded-xl">
                <span className="text-xs font-medium text-gray-900 truncate">{p.name}</span>
                <div className="text-right shrink-0">
                  <span className="text-xs font-bold text-[#FF3B30]">{p.current_stock}</span>
                  <span className="text-[10px] text-[#8E8E93]"> / {p.min_stock}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Expiring Products */}
      {data.expiring?.length > 0 && (
        <div className="ios-card">
          <div className="flex items-center gap-2 mb-3">
            <Clock size={14} className="text-[#FF9500]" />
            <p className="text-xs font-semibold text-[#FF9500] uppercase tracking-wider">SKT Yaklaşan ({data.expiring.length})</p>
          </div>
          <div className="space-y-1">
            {data.expiring.map((p, i) => (
              <div key={i} className="flex items-center justify-between px-2.5 py-1.5 bg-orange-50 rounded-xl">
                <div className="min-w-0">
                  <span className="text-xs font-medium text-gray-900 truncate block">{p.name}</span>
                  <span className="text-[10px] text-[#8E8E93]">Stok: {p.current_stock}</span>
                </div>
                <span className="text-xs font-bold text-[#FF9500] shrink-0">{formatDT(p.expiry_date)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stock Movements */}
      {data.movements?.length > 0 && (
        <div className="ios-card">
          <div className="flex items-center gap-2 mb-3">
            <ArrowUpDown size={14} className="text-[#6b6de3]" />
            <p className="text-xs font-semibold text-[#8E8E93] uppercase tracking-wider">Son Stok Hareketleri</p>
          </div>
          <div className="space-y-1">
            {data.movements.slice(0, 15).map((m, i) => (
              <div key={i} className="flex items-center gap-2 px-2.5 py-1.5 bg-[#F2F2F7] rounded-xl">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[9px] font-semibold px-1.5 py-px rounded-full bg-[#AF52DE]/10 text-[#AF52DE]">{ACTION_LABELS[m.action] || m.action}</span>
                  </div>
                  <p className="text-xs font-medium text-gray-900 truncate mt-0.5">{m.target_name}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[10px] text-[#8E8E93]">{formatDT(m.created_at)}</p>
                  {m.user_name && <p className="text-[9px] text-[#C7C7CC]">{m.user_name}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
